import React from 'react'
import {
  Button,
  Row,
  Col,
  Table,
  ButtonGroup,
  Accordion,
  Modal,
  Form
} from '@themesberg/react-bootstrap'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faEdit, faEye, faTrash } from '@fortawesome/pro-solid-svg-icons'

import Preloader from '../../../components/Preloader'
import useEmailTemplate from './hooks/useEmailTemplate'
import Trigger from '../../../components/OverlayTrigger'
import { useNavigate } from 'react-router-dom'
import { TenantRoutes } from '../../../routes'
import DOMPurify from 'dompurify'
import LicenseSelector from '../../../components/LicenseSelector'
import { DeleteConfirmationModal } from '../../../components/ConfirmationModal'

export default () => {
  const navigate = useNavigate()
  const {
    emailTemplates,
    templateCount,
    loading,
    show,
    setShow,
    setModalData,
    templateData,
    isHidden,
    emailTemplateOrder,
    lang,
    setLang,
    languages,
    resetEmail,
    type,
    emailTemplatesControlKeys,
    inactiveEmailTemplates,
    onChangeEmailTemplateStatus,
    isTenant,
    licenseListOptions,
    selectedLicense,
    setSelectedLicense,
    showDeleteEmailTemplateModal,
    setShowDeleteEmailTemplateModal,
    emailTemplateDeleteLoader,
    deleteEmailTemplateData,
    setDeleteEmailTemplateData,
    handleDeleteEmailTemplate
  } = useEmailTemplate()

  const isEmailTemplateActive = (emailTemplateName) => !inactiveEmailTemplates?.includes(emailTemplateName)

  return (
    <>
      <Row className='my-3'>
        <Col>
          <h3>Email Templates</h3>
        </Col>
        <Col>
          <Col>
            <div className='d-flex justify-content-end align-items-center'>
              {
                  isTenant && (
                    <div className='d-flex align-items-center justify-content-center mx-4'>
                      <LicenseSelector
                        options={licenseListOptions}
                        value={selectedLicense}
                        onChange={(e) => setSelectedLicense(e.target.value)}
                        selectClassName='form-select form-select-sm'
                        style={{ width: '220px' }}
                      />
                    </div>
                  )
              }
              <Button
                variant='outline-success'
                className='f-right'
                size='sm'
                hidden={isHidden({ module: { key: 'EmailTemplate', value: 'C' } })}
                onClick={() => {
                  navigate(TenantRoutes.CreateEmailTemplate)
                  resetEmail()
                }}
              >
                Create
              </Button>
            </div>
          </Col>
        </Col>
      </Row>
      {templateCount === 0 && !loading && (
        <Row className='d-flex justify-between-center mt-3'>
          <h4>No Templates Found</h4>
        </Row>
      )}
      <Accordion className='mt-3' defaultActiveKey='0'>
        {loading
          ? <Preloader />
          : emailTemplateOrder?.map(
            (template, index) => {
              const isActive = isEmailTemplateActive(template)

              return emailTemplates?.hasOwnProperty(template) && (
                <Accordion.Item
                  eventKey={String(index)}
                  key={`${template}-${index}`}
                >
                  <Accordion.Header>
                    <p className='w-100 d-flex justify-content-between align-items-center mb-0'>
                      <span className='d-flex gap-2 align-items-center'>
                        <label>{template}</label>
                        <span className={`p-1 border border-light rounded-circle ${isActive ? 'bg-success' : 'bg-danger'}`} />
                      </span>

                      {/* Add support to enable/disable specific email templates */}
                      {emailTemplatesControlKeys?.includes(template) && (
                        <Trigger message={`${isActive ? 'Disable' : 'Enable'} ${template} emails`}>
                          <Button
                            className='mx-2'
                            size='xs'
                            variant={isActive ? 'danger' : 'success'}
                            onClick={(e) => {
                              e.stopPropagation()
                              onChangeEmailTemplateStatus(template, !isActive)
                            }}
                            hidden={isHidden({ module: { key: 'EmailTemplate', value: 'U' } })}
                          >
                            {isActive ? 'Disable' : 'Enable'}
                          </Button>
                        </Trigger>
                      )}
                    </p>
                  </Accordion.Header>
                  <Accordion.Body>
                    <Table
                      bordered
                      striped
                      responsive
                      hover
                      size='sm'
                      className='text-center mt-4'
                    >
                      <thead className='thead-dark'>
                        <tr>
                          {['Id', 'Label', 'License', 'Action'].map((c) => (
                            <th key={c}>{c}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {emailTemplates?.[`${template}`].map(
                          ({
                            emailTemplateId,
                            label,
                            templateCode,
                            isPrimary,
                            type,
                            isDefault,
                            license,
                            licenseId,
                            tenantId
                          }) => {
                            return (
                              <tr key={emailTemplateId}>
                                <td>{emailTemplateId}</td>
                                <td>{label}</td>
                                <td>{license?.name || 'N/A'}</td>
                                <td>
                                  <ButtonGroup>
                                    {!isDefault && (
                                      <Trigger message='Edit'>
                                        <Button
                                          className='m-1'
                                          size='sm'
                                          variant='warning'
                                          hidden={isHidden({ module: { key: 'EmailTemplate', value: 'U' } })}
                                          onClick={() =>
                                            navigate(
                                            `/tenant/email-templates/edit/${emailTemplateId}`
                                            )}
                                        >
                                          <FontAwesomeIcon icon={faEdit} />
                                        </Button>
                                      </Trigger>
                                    )}

                                    <Trigger message='View Template'>
                                      <Button
                                        className='m-1'
                                        size='sm'
                                        variant='info'
                                        onClick={() =>
                                          setModalData(template,
                                            templateCode
                                          )}
                                      >
                                        <FontAwesomeIcon icon={faEye} />
                                      </Button>
                                    </Trigger>
                                    <Trigger message='Delete'>
                                      <Button
                                        className='m-1'
                                        size='sm'
                                        variant='danger'
                                        hidden={isHidden({ module: { key: 'EmailTemplate', value: 'U' } })}
                                        disabled={isPrimary}
                                        onClick={() => {
                                          setDeleteEmailTemplateData({ tenantId, emailTemplateId, licenseId, label })
                                          setShowDeleteEmailTemplateModal(true)
                                        }}
                                      >
                                        <FontAwesomeIcon icon={faTrash} />
                                      </Button>
                                    </Trigger>

                                  </ButtonGroup>
                                </td>
                              </tr>
                            )
                          }
                        )}
                      </tbody>
                    </Table>
                  </Accordion.Body>
                </Accordion.Item>
              )
            }

          )}
      </Accordion>

      <Modal
        show={show}
        onHide={() => setShow(false)}
        dialogClassName='modal-90w'
        size='lg'
        aria-labelledby='example-custom-modal-styling-title'
      >
        <Modal.Header closeButton>
          <Col>
            <h4>{type}</h4>
          </Col>
          <Col className='m-2'>
            <Form.Select
              name='language'
              size='sm'
              className='w-auto float-end'
              value={lang}
              onChange={(e) => {
                setLang(e.target.value)
              }}
            >
              <option value='EN'>English</option>
              {languages?.length && languages?.map((code) => {
                return code !== 'EN' && templateData?.[code] !== undefined && (
                  <option key={code} value={code}>{code}</option>
                )
              })}
            </Form.Select>
          </Col>
        </Modal.Header>
        <Modal.Body>
          <div
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(templateData?.[lang])
            }}
          />
        </Modal.Body>
      </Modal>

      {showDeleteEmailTemplateModal &&
        <DeleteConfirmationModal
          handleDeleteYes={handleDeleteEmailTemplate}
          setDeleteModalShow={() => setShowDeleteEmailTemplateModal(false)}
          deleteModalShow={showDeleteEmailTemplateModal}
          disabled={emailTemplateDeleteLoader}
          name={`this email template "${deleteEmailTemplateData.label}"`}
        />}
    </>
  )
}
